// Ultimate Card Game (UCG) - 8-Bit Animated Commander Avatar Renderer

export class AvatarRenderer {
  constructor(game) {
    this.game = game;
    this.canvasWestern = typeof document !== 'undefined' ? document.getElementById('western-avatar-canvas') : null;
    this.canvasEastern = typeof document !== 'undefined' ? document.getElementById('eastern-avatar-canvas') : null;

    this.ctxWestern = this.canvasWestern ? this.canvasWestern.getContext('2d') : null;
    this.ctxEastern = this.canvasEastern ? this.canvasEastern.getContext('2d') : null;

    this.playerRace = 'Human';
    this.enemyRace = 'Sul';

    // Particle motes for portraits
    this.westernParticles = [];
    this.easternParticles = [];

    // Commander transmission interference distress timers
    this.distressTimers = { western: 0, eastern: 0 };

    this._reusableGrid = Array.from({ length: 16 }, () => Array(16).fill(null));
    this._bgGrads = new Map();
    this._motePool = [];
  }

  triggerDistress(team, damage = 50) {
    if (!this.distressTimers) {
      this.distressTimers = { western: 0, eastern: 0 };
    }
    if (team === 'western' || team === 'eastern') {
      const added = Math.min(0.40, 0.18 + (damage / 120) * 0.18);
      this.distressTimers[team] = Math.max(this.distressTimers[team] || 0, added);
    }
  }

  setDecks(playerDeckId, enemyDeckId) {
    const pDeck = this.game.cardSystem?.teamRaces?.western || this.game.cardSystem?.decks?.western || playerDeckId || 'Human';
    const eDeck = this.game.cardSystem?.teamRaces?.eastern || this.game.cardSystem?.decks?.eastern || enemyDeckId || 'Sul';

    this.playerRace = this.getRaceFromDeck(pDeck);
    this.enemyRace = this.getRaceFromDeck(eDeck);
  }

  getRaceFromDeck(deckIdOrName) {
    if (!deckIdOrName) return 'Human';
    const lower = String(deckIdOrName).toLowerCase();
    if (lower.includes('sul')) return 'Sul';
    return 'Human';
  }

  update(dt) {
    if (this.distressTimers?.western > 0) {
      this.distressTimers.western = Math.max(0, this.distressTimers.western - (dt || 0.016));
    }
    if (this.distressTimers?.eastern > 0) {
      this.distressTimers.eastern = Math.max(0, this.distressTimers.eastern - (dt || 0.016));
    }

    this._renderAccumulator = (this._renderAccumulator || 0) + (dt || 0.016);
    // On low quality, throttle 8-bit retro portrait to 10 FPS (100ms); on higher tiers, 20 FPS (50ms).
    // During hit distress, boost to 30 FPS (33ms) so transmission glitch scanlines animate crisply.
    const isLow = this.game.renderer?.quality === 'low';
    const hasDistress = (this.distressTimers?.western > 0) || (this.distressTimers?.eastern > 0);
    const interval = hasDistress ? 0.033 : (isLow ? 0.10 : 0.05);
    if (this._renderAccumulator < interval) return;
    this._renderAccumulator = 0;

    // Sync current active deck races
    if (this.game.cardSystem?.decks?.western) {
      this.playerRace = this.getRaceFromDeck(this.game.cardSystem.decks.western);
    }
    if (this.game.cardSystem?.decks?.eastern) {
      this.enemyRace = this.getRaceFromDeck(this.game.cardSystem.decks.eastern);
    }

    this.render();
  }

  render() {
    const t = performance.now() * 0.001;
    const baseW = this.game.battlefield?.bases?.western;
    const baseE = this.game.battlefield?.bases?.eastern;

    const hpRatioW = baseW ? baseW.hp / baseW.maxHp : 1.0;
    const hpRatioE = baseE ? baseE.hp / baseE.maxHp : 1.0;

    if (this.ctxWestern && this.canvasWestern) {
      this.renderAvatar(this.ctxWestern, this.canvasWestern, this.playerRace, 'western', t, hpRatioW, this.westernParticles);
    }

    if (this.ctxEastern && this.canvasEastern) {
      this.renderAvatar(this.ctxEastern, this.canvasEastern, this.enemyRace, 'eastern', t, hpRatioE, this.easternParticles);
    }
  }

  renderAvatar(ctx, canvas, race, team, t, hpRatio, particles) {
    const w = canvas.width;
    const h = canvas.height;
    const isWestern = team === 'western';
    const isCritical = hpRatio < 0.25;
    const distress = this.distressTimers?.[team] || 0;
    const isDistressed = distress > 0;

    // Fast-path: When an opaque custom commander portrait <img> covers the canvas
    // and neither distress static nor critical alert overlay is active, skip all
    // background grid, particle FX, and bracket drawing behind the image.
    const customImg = typeof document !== 'undefined' ? document.getElementById(`${team}-avatar-custom-image`) : null;
    const hasCustomAvatar = !!(customImg && !customImg.classList.contains('hidden') && customImg.getAttribute('src'));

    if (customImg) {
      customImg.classList.toggle('avatar-distress-glitch', isDistressed);
    }

    if (hasCustomAvatar && !isDistressed && !isCritical) {
      if (!canvas._clearedForCustom) {
        ctx.clearRect(0, 0, w, h);
        canvas._clearedForCustom = true;
      }
      return;
    }
    canvas._clearedForCustom = false;

    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = false;

    // 1. Cybernetic HUD Background Grid
    const bgKey = `${race}_${team}_${h}`;
    let bgGrad = this._bgGrads.get(bgKey);
    if (!bgGrad) {
      bgGrad = ctx.createLinearGradient(0, 0, 0, h);
      if (race === 'Sul') {
        bgGrad.addColorStop(0, '#1c052e');
        bgGrad.addColorStop(1, '#090112');
      } else if (isWestern) {
        bgGrad.addColorStop(0, '#0a1526');
        bgGrad.addColorStop(1, '#050a14');
      } else {
        bgGrad.addColorStop(0, '#260a12');
        bgGrad.addColorStop(1, '#140508');
      }
      this._bgGrads.set(bgKey, bgGrad);
    }
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);

    // Subtle background radar sweep / grid
    if (race === 'Sul') {
      ctx.fillStyle = 'rgba(217, 70, 239, 0.08)';
    } else {
      ctx.fillStyle = isWestern ? 'rgba(0, 229, 255, 0.06)' : 'rgba(255, 51, 68, 0.06)';
    }
    for (let gy = 0; gy < h; gy += 6) {
      ctx.fillRect(0, gy, w, 1);
    }
    for (let gx = 0; gx < w; gx += 6) {
      ctx.fillRect(gx, 0, 1, h);
    }

    // 2. Render Procedural 8-Bit Pixel Character (only when no custom avatar is equipped)
    if (!hasCustomAvatar) {
      const gridSize = 16;
      const pSize = 3; // 16 * 3 = 48px
      const grid = this._reusableGrid;
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          grid[gy][gx] = null;
        }
      }

      // Breathing & Motion Dynamics
      const breathOffset = Math.round(Math.sin(t * 3.2 + (isWestern ? 0 : 1.5)) * 0.8);
      const blinkCycle = (t * 0.8 + (isWestern ? 0 : 2.5)) % 4.0;
      const isBlinking = blinkCycle < 0.18;
      const glintPos = Math.floor((t * 5.5 + (isWestern ? 0 : 2.0)) % 18) - 2;

      // Jitter displacement on certain rows during hit distress
      const glitchActive = isDistressed && (Math.sin(t * 45 + (isWestern ? 0 : 3.2)) > 0.15);
      const glitchShift = glitchActive ? Math.round(Math.sin(t * 85) * 1.5) : 0;
      const glitchRow = glitchActive ? Math.floor(Math.abs(Math.sin(t * 31) * gridSize)) : -1;

      if (race === 'Sul') {
        this.buildSulGrid(grid, isWestern, isBlinking, t);
      } else {
        this.buildHumanGrid(grid, isWestern, isBlinking, glintPos, t);
      }

      // Draw the 8-bit grid with pixel offsets
      for (let gy = 0; gy < gridSize; gy++) {
        for (let gx = 0; gx < gridSize; gx++) {
          const color = grid[gy][gx];
          if (color) {
            // Apply breathing displacement to upper torso & head
            const drawY = (gy + (gy >= 2 && gy <= 14 ? breathOffset : 0)) * pSize;
            const shiftX = (gy === glitchRow || (glitchActive && gy >= 3 && gy <= 7)) ? glitchShift : 0;
            const drawX = (gx + shiftX) * pSize;

            ctx.fillStyle = color;
            ctx.fillRect(drawX, drawY, pSize, pSize);

            // Top pixel specular highlight
            if (gy > 0 && !grid[gy - 1][gx]) {
              ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
              ctx.fillRect(drawX, drawY, pSize, 1);
            }
          }
        }
      }
    }

    // 3. Dynamic Foreground Lighting & Particles
    this.renderAvatarFX(ctx, w, h, race, team, t, isCritical, particles);

    // 3B. Commander Transmission Radio Interference (Flimmerframes on Base Hit)
    if (isDistressed) {
      const seed1 = Math.sin(t * 67 + (isWestern ? 1.0 : 4.2));
      const seed2 = Math.cos(t * 53 + (isWestern ? 2.5 : 0.8));
      const staticY1 = Math.floor(Math.abs(seed1) * (h - 6)) + 2;
      const staticY2 = Math.floor(Math.abs(seed2) * (h - 6)) + 2;

      // 1. Horizontal chromatic static interference band
      ctx.fillStyle = isWestern ? 'rgba(0, 229, 255, 0.55)' : 'rgba(255, 51, 68, 0.55)';
      ctx.fillRect(0, staticY1, w, 2);

      // 2. Bright white analog static sparkles along scanlines
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      const noiseX1 = Math.floor(Math.abs(Math.sin(t * 97)) * (w - 14));
      ctx.fillRect(noiseX1, staticY1, 12, 1);
      const noiseX2 = Math.floor(Math.abs(Math.cos(t * 83)) * (w - 8));
      ctx.fillRect(noiseX2, staticY2, 7, 1);
    }

    // 4. Critical Damage Red Alert Warning Flash
    if (isCritical) {
      const alertAlpha = 0.25 + 0.25 * Math.sin(t * 8);
      ctx.fillStyle = `rgba(255, 0, 40, ${alertAlpha})`;
      ctx.fillRect(0, 0, w, h);

      // Warning text badge
      ctx.font = 'bold 8px monospace';
      ctx.fillStyle = '#ff3344';
      ctx.textAlign = 'center';
      ctx.fillText('CRITICAL', w / 2, 9);
    }

    // 5. Sci-Fi Corner Brackets
    const bracketColor = race === 'Sul' ? '#d946ef' : (isWestern ? '#00e5ff' : '#ff3344');
    ctx.fillStyle = bracketColor;
    // Top-Left
    ctx.fillRect(1, 1, 4, 1); ctx.fillRect(1, 1, 1, 4);
    // Top-Right
    ctx.fillRect(w - 5, 1, 4, 1); ctx.fillRect(w - 2, 1, 1, 4);
    // Bottom-Left
    ctx.fillRect(1, h - 2, 4, 1); ctx.fillRect(1, h - 5, 1, 4);
    // Bottom-Right
    ctx.fillRect(w - 5, h - 2, 4, 1); ctx.fillRect(w - 2, h - 5, 1, 4);
  }

  buildHumanGrid(grid, isWestern, isBlinking, glintPos, t) {
    const teamMain = isWestern ? '#2563eb' : '#dc2626';
    const teamHighlight = isWestern ? '#60a5fa' : '#f87171';
    const teamDark = isWestern ? '#1d4ed8' : '#991b1b';

    // 1. Helmet Crown & Antenna
    grid[1][2] = '#64748b'; // Antenna base
    grid[0][2] = Math.sin(t * 6) > 0 ? '#22c55e' : '#15803d'; // Blinking Comm Beacon
    for (let x = 5; x <= 10; x++) grid[1][x] = '#1e293b';
    for (let x = 4; x <= 11; x++) grid[2][x] = '#334155';
    grid[2][7] = teamHighlight; grid[2][8] = teamHighlight; // Commander Stripe

    // 2. Forehead & Headset Earpiece
    for (let x = 4; x <= 11; x++) grid[3][x] = teamMain;
    grid[3][3] = '#475569'; grid[3][12] = '#475569'; // Headset Cups

    // 3. Golden Tactical Visor (Row 4 & 5)
    for (let x = 4; x <= 11; x++) {
      grid[4][x] = '#f59e0b';
      grid[5][x] = '#d97706';
    }
    // Visor Glint Reflection
    if (glintPos >= 4 && glintPos <= 11) {
      grid[4][glintPos] = '#ffffff';
      if (glintPos + 1 <= 11) grid[4][glintPos + 1] = '#fef08a';
    }
    if (isBlinking) {
      for (let x = 4; x <= 11; x++) { grid[4][x] = '#78350f'; grid[5][x] = '#451a03'; }
    }

    // 4. Respirator Mask & Mic Boom (Row 6 & 7)
    for (let x = 5; x <= 10; x++) grid[6][x] = '#475569';
    grid[6][3] = '#94a3b8'; grid[6][4] = '#cbd5e1'; // Mic Arm
    grid[7][5] = '#1e293b'; grid[7][6] = '#64748b'; grid[7][9] = '#64748b'; grid[7][10] = '#1e293b'; // Air Vents
    grid[7][7] = '#0f172a'; grid[7][8] = '#0f172a';

    // 5. Neck Seal & Collar (Row 8)
    for (let x = 5; x <= 10; x++) grid[8][x] = '#1e293b';

    // 6. Heavy CMC Pauldrons & Rank Insignia (Rows 9 to 12)
    for (let y = 9; y <= 12; y++) {
      for (let x = 1; x <= 4; x++) grid[y][x] = (x === 1 || y === 9) ? teamHighlight : teamMain;
      for (let x = 11; x <= 14; x++) grid[y][x] = (x === 14 || y === 9) ? teamHighlight : teamMain;
    }
    grid[10][2] = '#facc15'; grid[11][2] = '#facc15'; // Gold Rank Stripes
    grid[10][13] = '#facc15'; grid[11][13] = '#facc15';

    // 7. Chestplate & Micro Reactor (Rows 9 to 15)
    for (let y = 9; y <= 15; y++) {
      for (let x = 5; x <= 10; x++) grid[y][x] = '#1e293b';
    }
    for (let y = 10; y <= 14; y++) {
      for (let x = 6; x <= 9; x++) grid[y][x] = teamDark;
    }
    // Reactor Core Glow
    const reactorColor = isWestern ? (Math.sin(t * 5) > 0 ? '#00f0ff' : '#38bdf8') : (Math.sin(t * 5) > 0 ? '#ff3344' : '#ff7788');
    grid[12][7] = '#ffffff'; grid[12][8] = reactorColor;
    grid[13][7] = reactorColor; grid[13][8] = '#ffffff';
  }

  buildSulGrid(grid, isWestern, isBlinking, t) {
    const goldBright = '#fef08a';
    const goldLight = '#fde047';
    const goldMid = '#eab308';
    const goldDark = '#854d0e';
    const chitinDark = '#160824';
    const chitinMid = '#2e1053';
    const chitinLight = '#4a154b';
    const plasmaPink = '#ff0088';
    const plasmaPurple = '#d946ef';
    const fleshMauve = '#831843';
    const opticCyan = '#00f0ff';
    const opticWhite = '#ffffff';
    const rootDark = '#701a75';
    const rootLight = '#a21caf';

    // 1. Spreading Antler Horns / Telepathic Crest (Rows 0 to 3)
    grid[0][2] = goldBright; grid[0][4] = goldMid;
    grid[0][11] = goldMid; grid[0][13] = goldBright;

    grid[1][2] = goldMid; grid[1][3] = goldBright; grid[1][4] = goldMid;
    grid[1][7] = goldMid; grid[1][8] = goldMid;
    grid[1][11] = goldMid; grid[1][12] = goldBright; grid[1][13] = goldMid;

    grid[2][3] = goldMid; grid[2][4] = goldDark;
    grid[2][6] = goldLight; grid[2][7] = '#ffffff'; grid[2][8] = '#ffffff'; grid[2][9] = goldLight;
    grid[2][11] = goldDark; grid[2][12] = goldMid;

    grid[3][4] = goldDark; grid[3][11] = goldDark;
    for (let x = 5; x <= 10; x++) grid[3][x] = chitinMid;
    grid[3][7] = goldLight; grid[3][8] = goldLight;

    // 2. Bread Loaf 𓏏 Dome Skull & Chitin Visage (Rows 4 to 8)
    for (let x = 4; x <= 11; x++) grid[4][x] = chitinMid;
    grid[4][4] = chitinDark; grid[4][11] = chitinDark;
    grid[4][7] = plasmaPink; grid[4][8] = plasmaPink;

    // Glowing Optic Eye Slits & Third Eye Plasma Gem (Rows 5 & 6)
    for (let x = 3; x <= 12; x++) grid[5][x] = chitinMid;
    grid[5][3] = chitinDark; grid[5][12] = chitinDark;
    if (!isBlinking) {
      grid[5][5] = opticCyan; grid[5][6] = opticWhite;
      grid[5][9] = opticWhite; grid[5][10] = opticCyan;
    } else {
      grid[5][5] = chitinDark; grid[5][6] = chitinDark;
      grid[5][9] = chitinDark; grid[5][10] = chitinDark;
    }
    grid[5][7] = '#ffffff'; grid[5][8] = plasmaPink;

    for (let x = 4; x <= 11; x++) grid[6][x] = chitinMid;
    grid[6][4] = chitinDark; grid[6][11] = chitinDark;
    if (!isBlinking) {
      grid[6][5] = opticCyan; grid[6][6] = opticCyan;
      grid[6][9] = opticCyan; grid[6][10] = opticCyan;
    } else {
      grid[6][5] = chitinDark; grid[6][6] = chitinDark;
      grid[6][9] = chitinDark; grid[6][10] = chitinDark;
    }
    grid[6][7] = chitinLight; grid[6][8] = chitinLight;

    // Resonating Guttural Throat Cavity (Row 7)
    for (let x = 5; x <= 10; x++) grid[7][x] = chitinDark;
    grid[7][4] = goldDark; grid[7][11] = goldDark;
    const throatGlow = Math.sin(t * 8) > 0 ? plasmaPurple : plasmaPink;
    grid[7][7] = throatGlow; grid[7][8] = throatGlow;

    // Mandibles (Row 8)
    grid[8][4] = goldMid; grid[8][11] = goldMid;
    for (let x = 5; x <= 10; x++) grid[8][x] = chitinDark;
    grid[8][7] = fleshMauve; grid[8][8] = fleshMauve;

    // 3. Spiked Chitin Mantle & Gold Thorns (Rows 9 to 12)
    grid[9][1] = goldBright; grid[9][14] = goldBright;
    for (let x = 2; x <= 4; x++) grid[9][x] = chitinMid;
    for (let x = 5; x <= 10; x++) grid[9][x] = chitinDark;
    for (let x = 11; x <= 13; x++) grid[9][x] = chitinMid;

    grid[10][1] = goldMid; grid[10][14] = goldMid;
    for (let x = 2; x <= 4; x++) grid[10][x] = chitinMid;
    for (let x = 5; x <= 10; x++) grid[10][x] = chitinDark;
    grid[10][6] = goldMid; grid[10][9] = goldMid;
    for (let x = 11; x <= 13; x++) grid[10][x] = chitinMid;

    // Pulsing Bio-Heart Reactor
    for (let x = 2; x <= 4; x++) grid[11][x] = chitinDark;
    for (let x = 5; x <= 10; x++) grid[11][x] = chitinDark;
    for (let x = 11; x <= 13; x++) grid[11][x] = chitinDark;
    const heartPulse = Math.sin(t * 6) > 0;
    grid[11][7] = '#ffffff'; grid[11][8] = heartPulse ? plasmaPink : plasmaPurple;

    for (let x = 5; x <= 10; x++) grid[12][x] = chitinDark;
    grid[12][3] = fleshMauve; grid[12][12] = fleshMauve;
    grid[12][7] = heartPulse ? plasmaPurple : plasmaPink; grid[12][8] = '#ffffff';

    // 4. Hanging Mycelium Roots & Bioluminescent Tips (Rows 13 to 15)
    const sway1 = Math.max(-1, Math.min(1, Math.round(Math.sin(t * 3.5) * 0.5)));
    grid[13][2 + sway1] = rootLight;
    grid[13][4] = rootDark;
    grid[13][6] = fleshMauve; grid[13][7] = chitinDark; grid[13][8] = chitinDark; grid[13][9] = fleshMauve;
    grid[13][11] = rootDark;
    grid[13][13 - sway1] = rootLight;

    grid[14][2 + sway1] = rootDark;
    grid[14][4] = rootLight;
    grid[14][7] = rootDark; grid[14][8] = rootDark;
    grid[14][11] = rootLight;
    grid[14][13 - sway1] = rootDark;

    grid[15][2 + sway1] = opticCyan;
    grid[15][4] = plasmaPurple;
    grid[15][11] = plasmaPurple;
    grid[15][13 - sway1] = opticCyan;
  }

  renderAvatarFX(ctx, w, h, race, team, t, isCritical, particles) {
    const isWestern = team === 'western';
    const fxColor = race === 'Sul' ? '#d946ef' : (isWestern ? '#00e5ff' : '#ff3344');

    // 1. Occasional floating aura motes
    if (Math.random() < 0.15 && particles.length < 12) {
      let p = this._motePool.pop();
      if (!p) {
        p = { x: 0, y: 0, vx: 0, vy: 0, alpha: 0, size: 1 };
      }
      p.x = 6 + Math.random() * (w - 12);
      p.y = h - 4;
      p.vx = (Math.random() - 0.5) * 6;
      p.vy = -(10 + Math.random() * 14);
      p.alpha = 0.8;
      p.size = Math.random() > 0.5 ? 2 : 1;
      particles.push(p);
    }

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx * (1 / 60);
      p.y += p.vy * (1 / 60);
      p.alpha -= 0.025;

      if (p.alpha <= 0 || p.y < 0) {
        particles.splice(i, 1);
        if (this._motePool.length < 32) {
          this._motePool.push(p);
        }
        continue;
      }

      ctx.fillStyle = fxColor;
      ctx.globalAlpha = p.alpha;
      ctx.fillRect(Math.floor(p.x), Math.floor(p.y), p.size, p.size);
    }
    ctx.globalAlpha = 1.0;

    // 2. Scanline Shimmer
    ctx.fillStyle = 'rgba(255, 255, 255, 0.04)';
    const scanY = Math.floor((t * 24) % h);
    ctx.fillRect(0, scanY, w, 2);
  }
}
